/* HOUND — Title Screen Target Cursor
   Vanilla (GSAP-free) port of the ReactBits "Target Cursor". A small reticle —
   a centre dot plus four corner brackets — replaces the OS cursor on the title
   screen. Idle, it spins slowly; when the pointer is over a menu button the
   brackets fly out to frame the button's bounding box with a subtle trailing
   parallax, then snap back on leave.

   Scoping: the title screen, marketplace, guide, quest and world-map are all
   the SAME page (JS view-swaps), so the reticle is enabled only while the title
   menu is the active surface and is torn down the instant the player navigates
   into a sub-screen — it never bleeds into those UIs. Desktop fine-pointer only;
   skipped on touch and under prefers-reduced-motion. */
(function () {
  'use strict';

  var TARGET_SELECTOR = '.custom-buttons-container .btn:not(.disabled)';
  var COLOR  = '#e8d7a9';  // brand parchment-gold — matches the menu text
  var CORNER = 12;         // corner bracket size (px)
  var BORDER = 3;          // bracket stroke width (px)
  var PAD    = 3;          // breathing room outside the framed button (px)
  var SPIN_MS = 4000;      // ms per idle revolution (calm)

  // Resting corner transl(reticle around the dot), mirroring the original CSS
  // base transforms: tl(-1.5,-1.5) tr(.5,-1.5) br(.5,.5) bl(-1.5,.5) of CORNER.
  var REST = [
    { x: -CORNER * 1.5, y: -CORNER * 1.5 },
    { x:  CORNER * 0.5, y: -CORNER * 1.5 },
    { x:  CORNER * 0.5, y:  CORNER * 0.5 },
    { x: -CORNER * 1.5, y:  CORNER * 0.5 }
  ];

  var reduced    = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isTouch    = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;
  var finePtr    = window.matchMedia('(pointer: fine)').matches;
  if (reduced || isTouch || !finePtr) return;

  var wrap, dot, corners = [];
  var built = false, enabled = false, rafId = null, lastT = 0;

  // wrapper position (eased toward the mouse)
  var px = window.innerWidth / 2, py = window.innerHeight / 2, mx = px, my = py;
  var rot = 0;             // wrapper rotation (deg)
  var strength = 0;        // 0..1 framing ramp
  var press = 0, pressT = 0; // mousedown squash
  var cx = [], cy = [];    // current corner translate
  var activeTarget = null, framePos = null;

  function build() {
    if (built) return;
    var s = document.createElement('style');
    s.id = 'hound-title-cursor-style';
    // NB: rules are scoped under .htc-wrap so they out-specify the theme's
    // `div:empty{display:none}` reset (these reticle nodes are intentionally
    // empty); display:block is set explicitly for the same reason.
    s.textContent =
      '.htc-wrap{position:fixed;top:0;left:0;width:0;height:0;pointer-events:none;' +
        'z-index:99999;opacity:0;transition:opacity .3s ease;}' +
      '.htc-wrap.on{opacity:1;}' +
      '.htc-wrap .htc-dot{display:block;position:absolute;left:0;top:0;width:5px;height:5px;' +
        'border-radius:50%;background:' + COLOR + ';transform:translate(-50%,-50%);' +
        'box-shadow:0 0 6px rgba(232,215,169,.75);}' +
      '.htc-wrap .htc-corner{display:block;position:absolute;left:0;top:0;' +
        'width:' + CORNER + 'px;height:' + CORNER + 'px;' +
        'border:' + BORDER + 'px solid ' + COLOR + ';filter:drop-shadow(0 0 3px rgba(0,0,0,.5));}' +
      '.htc-wrap .htc-corner.htc-tl{border-right:none;border-bottom:none;}' +
      '.htc-wrap .htc-corner.htc-tr{border-left:none;border-bottom:none;}' +
      '.htc-wrap .htc-corner.htc-br{border-left:none;border-top:none;}' +
      '.htc-wrap .htc-corner.htc-bl{border-right:none;border-top:none;}' +
      'html.htc-on,html.htc-on body,html.htc-on a,html.htc-on .btn{cursor:none;}';
    document.head.appendChild(s);

    wrap = document.createElement('div');
    wrap.className = 'htc-wrap';
    wrap.setAttribute('aria-hidden', 'true');
    dot = document.createElement('div');
    dot.className = 'htc-dot';
    wrap.appendChild(dot);
    ['tl', 'tr', 'br', 'bl'].forEach(function (k, i) {
      var c = document.createElement('div');
      c.className = 'htc-corner htc-' + k;
      wrap.appendChild(c);
      corners[i] = c;
      cx[i] = REST[i].x;
      cy[i] = REST[i].y;
    });
    document.body.appendChild(wrap);
    built = true;
  }

  // frame-rate independent exponential smoothing
  function ease(cur, target, tau, dt) {
    return cur + (target - cur) * (1 - Math.exp(-dt / tau));
  }

  function computeFrame() {
    var r = activeTarget.getBoundingClientRect();
    return [
      { x: r.left  - BORDER - PAD,            y: r.top    - BORDER - PAD },
      { x: r.right + BORDER + PAD - CORNER,   y: r.top    - BORDER - PAD },
      { x: r.right + BORDER + PAD - CORNER,   y: r.bottom + BORDER + PAD - CORNER },
      { x: r.left  - BORDER - PAD,            y: r.bottom + BORDER + PAD - CORNER }
    ];
  }

  function setActive(tgt) {
    if (tgt === activeTarget) return;
    activeTarget = tgt;
    // snap rotation to the nearest full turn (visually 0) so the framed brackets
    // sit square to the button, matching the original's hard rotation reset.
    if (tgt) rot = Math.round(rot / 360) * 360;
  }

  function frame(t) {
    if (!enabled) return;
    if (!lastT) lastT = t;
    var dt = Math.min(0.05, (t - lastT) / 1000);
    lastT = t;

    // hit-test under the pointer (custom cursor is pointer-events:none, so this
    // returns the real element beneath it)
    var el = document.elementFromPoint(mx, my);
    setActive(el ? el.closest(TARGET_SELECTOR) : null);

    px = ease(px, mx, 0.045, dt);
    py = ease(py, my, 0.045, dt);
    press = ease(press, pressT, 0.05, dt);

    if (activeTarget) {
      strength = ease(strength, 1, 0.08, dt);
      framePos = computeFrame();
    } else {
      rot += (360 * dt * 1000) / SPIN_MS; // spin only while idle
      if (rot > 3600) rot -= 3600;
      strength = ease(strength, 0, 0.06, dt);
    }

    var sc = 1 - press * 0.1;
    wrap.style.transform = 'translate(' + px + 'px,' + py + 'px) rotate(' + rot + 'deg) scale(' + sc + ')';
    dot.style.transform = 'translate(-50%,-50%) scale(' + (1 - press * 0.3) + ')';

    for (var i = 0; i < 4; i++) {
      var tX = REST[i].x, tY = REST[i].y;
      if (framePos && strength > 0.001) {
        tX = REST[i].x + ((framePos[i].x - px) - REST[i].x) * strength;
        tY = REST[i].y + ((framePos[i].y - py) - REST[i].y) * strength;
      }
      cx[i] = ease(cx[i], tX, 0.05, dt);
      cy[i] = ease(cy[i], tY, 0.05, dt);
      corners[i].style.transform = 'translate(' + cx[i] + 'px,' + cy[i] + 'px)';
    }
    rafId = requestAnimationFrame(frame);
  }

  function onMove(e) { mx = e.clientX; my = e.clientY; }
  function onDown() { pressT = 1; }
  function onUp() { pressT = 0; }

  function enable() {
    if (enabled) return;
    build();
    enabled = true;
    document.documentElement.classList.add('htc-on');
    wrap.classList.add('on');
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mousedown', onDown);
    window.addEventListener('mouseup', onUp);
    lastT = 0;
    rafId = requestAnimationFrame(frame);
  }

  function disable() {
    if (!enabled) return;
    enabled = false;
    document.documentElement.classList.remove('htc-on');
    if (wrap) wrap.classList.remove('on');
    window.removeEventListener('mousemove', onMove);
    window.removeEventListener('mousedown', onDown);
    window.removeEventListener('mouseup', onUp);
    if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
    activeTarget = null;
    strength = 0;
  }

  // The title menu surface is identified by <html data-view> (the app's
  // documented source of truth). 'main-menu' is the title menu; an unset/empty
  // value is the initial pre-navigation title. Any other view (merchants-den,
  // guide, quest, world-map, idle) is a sub-screen where the reticle must not
  // appear. Driving off data-view — rather than the raw 'navigate' event —
  // means the reticle correctly returns when the player navigates BACK to the
  // menu (navigateTo('main-menu')), which the event-name approach missed.
  function isTitleMenu(v) { return !v || v === 'main-menu' || v === 'title'; }

  function syncToView() {
    if (isTitleMenu(document.documentElement.getAttribute('data-view'))) enable();
    else disable();
  }

  // Enable when the title screen reveals; if it is already visible (deferred
  // load raced the reveal), enable immediately.
  document.addEventListener('titleScreenRevealed', enable);
  var tc = document.getElementById('title-screen-content');
  if (tc && getComputedStyle(tc).opacity === '1') syncToView();

  new MutationObserver(syncToView).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-view']
  });
})();
